import { DoorOpening, DoorPoint, HexColor } from "../models";
import { Aspect } from "../ui/Aspect";
import { Dimension2D } from "../ui/Dimension2D";
import { RotationAngle } from "../ui/RotationAngle";
import { LoadDoorOptions } from "../ui/LoadDoorOptions";
import { LineFormula } from "../ui/LineFormula";

export class Calculator {
  public static calculateAspectRatioFit(
    aspectRatio: Dimension2D,
    boundingSize: Dimension2D
  ): Dimension2D {
    let width = boundingSize.width;
    let height = boundingSize.height;
    const mW = boundingSize.width / aspectRatio.width;
    const mH = boundingSize.height / aspectRatio.height;

    if (mH < mW) {
      width = (boundingSize.height / aspectRatio.height) * aspectRatio.width;
    } else if (mW < mH) {
      height = (boundingSize.width / aspectRatio.width) * aspectRatio.height;
    }

    return new Dimension2D(width, height);
  }

  public static calculateAspectRatioFill(
    aspectRatio: Dimension2D,
    minimumSize: Dimension2D
  ): Dimension2D {
    let width = minimumSize.width;
    let height = minimumSize.height;
    const mW = minimumSize.width / aspectRatio.width;
    const mH = minimumSize.height / aspectRatio.height;

    if (mH > mW) {
      width = (minimumSize.height / aspectRatio.height) * aspectRatio.width;
    } else if (mW > mH) {
      height = (minimumSize.width / aspectRatio.width) * aspectRatio.height;
    }

    return new Dimension2D(width, height);
  }

  public static calculateAspectRatio(
    aspect: Aspect,
    aspectRatio: Dimension2D,
    minimumSize: Dimension2D
  ): Dimension2D {
    if (aspect === Aspect.Fit) {
      return Calculator.calculateAspectRatioFit(aspectRatio, minimumSize);
    }

    return Calculator.calculateAspectRatioFill(aspectRatio, minimumSize);
  }

  public static calculateDistanceBetweenPointAndLine(
    linePoint1: { x: number; y: number },
    linePoint2: { x: number; y: number },
    point: { x: number; y: number }
  ): number {
    return (
      ((linePoint2.y - linePoint1.y) * point.x -
        (linePoint2.x - linePoint1.x) * point.y +
        linePoint2.x * linePoint1.y -
        linePoint2.y * linePoint1.x) /
      Math.sqrt(
        Math.pow(linePoint2.y - linePoint1.y, 2) +
          Math.pow(linePoint2.x - linePoint1.x, 2)
      )
    );
  }

  public static calculateLineFormula(
    linePoint1: DoorPoint,
    linePoint2: DoorPoint
  ): LineFormula {
    if (linePoint1.x === linePoint2.x) {
      return new LineFormula(Infinity, Infinity, linePoint1.x);
    }

    const slope = (linePoint2.y - linePoint1.y) / (linePoint2.x - linePoint1.x);
    const yOffset = linePoint1.y - slope * linePoint1.x;

    return new LineFormula(slope, yOffset);
  }

  public static calculateYOnLine(
    x: number,
    linePoint1: DoorPoint,
    linePoint2: DoorPoint
  ): number {
    return (
      ((linePoint2.y - linePoint1.y) / (linePoint2.x - linePoint1.x)) *
        (x - linePoint1.x) +
      linePoint1.y
    );
  }

  public static calculateDistanceBetweenTwoPoints(
    point1: DoorPoint,
    point2: DoorPoint
  ): number {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  public static calculateXOnLine(
    y: number,
    linePoint1: DoorPoint,
    linePoint2: DoorPoint
  ): number {
    return (
      ((linePoint2.x - linePoint1.x) / (linePoint2.y - linePoint1.y)) *
        (y - linePoint1.y) +
      linePoint1.x
    );
  }

  public static calculateDoorOpeningSize(
    doorOpening: DoorOpening,
    dimension: Dimension2D
  ): DoorOpening {
    const calculatedDoorOpening = new DoorOpening(
      doorOpening.doorType,
      Calculator.calculatePointPosition(doorOpening.topLeft, dimension),
      Calculator.calculatePointPosition(doorOpening.topRight, dimension),
      Calculator.calculatePointPosition(doorOpening.bottomLeft, dimension),
      Calculator.calculatePointPosition(doorOpening.bottomRight, dimension),
      Calculator.calculatePointPosition(doorOpening.chamferEdgeLeft, dimension),
      Calculator.calculatePointPosition(
        doorOpening.chamferEdgeRight,
        dimension
      ),
      Calculator.calculatePointPosition(doorOpening.archEdgeLeft, dimension)
    );

    return calculatedDoorOpening;
  }

  private static calculatePointPosition(
    point: DoorPoint | undefined,
    dimension: Dimension2D
  ): DoorPoint | undefined {
    if (!point) {
      return undefined;
    }

    return {
      x: point.x * dimension.width,
      y: point.y * dimension.height,
    };
  }

  public static calculateDoorOpeningRatio(
    doorOpening: DoorOpening,
    dimension: Dimension2D
  ): DoorOpening {
    const calculatedDoorOpening = new DoorOpening(
      doorOpening.doorType,
      Calculator.calculatePointRatio(doorOpening.topLeft, dimension),
      Calculator.calculatePointRatio(doorOpening.topRight, dimension),
      Calculator.calculatePointRatio(doorOpening.bottomLeft, dimension),
      Calculator.calculatePointRatio(doorOpening.bottomRight, dimension),
      Calculator.calculatePointRatio(doorOpening.chamferEdgeLeft, dimension),
      Calculator.calculatePointRatio(doorOpening.chamferEdgeRight, dimension),
      Calculator.calculatePointRatio(doorOpening.archEdgeLeft, dimension)
    );

    return calculatedDoorOpening;
  }

  private static calculatePointRatio(
    point: DoorPoint | undefined,
    dimension: Dimension2D
  ): DoorPoint | undefined {
    if (!point) {
      return undefined;
    }

    return {
      x: point.x / dimension.width,
      y: point.y / dimension.height,
    };
  }

  public static calculateLinesIntersection(
    lineFormula1: LineFormula,
    lineFormula2: LineFormula
  ): DoorPoint | null {
    if (lineFormula1.isVerticalAsymptote()) {
      return new DoorPoint(
        lineFormula1.xAsymptote,
        lineFormula2.calculateY(lineFormula1.xAsymptote)
      );
    }

    if (lineFormula2.isVerticalAsymptote()) {
      return new DoorPoint(
        lineFormula2.xAsymptote,
        lineFormula1.calculateY(lineFormula2.xAsymptote)
      );
    }

    const x =
      (lineFormula2.yIntercept - lineFormula1.yIntercept) /
      (lineFormula1.slope - lineFormula2.slope);
    const y = lineFormula1.calculateY(x);

    return new DoorPoint(x, y);
  }

  public static isImageFlipped(rotate: number): boolean {
    return rotate === 8 || rotate === 10 || rotate === 12 || rotate === 14;
  }

  public static calculateRotateValue(
    rotate: number,
    rotationAngle: RotationAngle,
    clockwise: boolean
  ): number {
    const isImageFlipped = Calculator.isImageFlipped(rotate);

    if (!isImageFlipped) {
      if (clockwise) {
        rotate = rotate + rotationAngle * -2;
      } else {
        rotate = rotate + rotationAngle * 2;
      }

      if (rotate == 8) {
        rotate = 0;
      } else if (rotate == -2) {
        rotate = 6;
      }
    } else {
      if (clockwise) {
        rotate = rotate + rotationAngle * 2;
      } else {
        rotate = rotate + rotationAngle * -2;
      }

      if (rotate == 16) {
        rotate = 8;
      } else if (rotate == 6) {
        rotate = 14;
      }
    }

    return rotate;
  }

  public static calculateFlipValue(
    rotate: number,
    vertically: boolean
  ): number {
    const isImageFlipped = Calculator.isImageFlipped(rotate);

    if (vertically) {
      if (!isImageFlipped) {
        rotate = rotate + 8;
      } else {
        rotate = rotate - 8;
      }
    } else {
      if (!isImageFlipped) {
        rotate = rotate + 12;

        if (rotate > 14) {
          rotate = rotate - 8;
        }
      } else {
        rotate = rotate - 12;

        if (rotate < 0) {
          rotate = rotate + 8;
        }
      }
    }

    return rotate;
  }

  public static generateDefaultLoadDoorOptions() {
    const loadDoorOptions: LoadDoorOptions = {
      isDoorSelectable: true,
      isDoorDraggable: true,
      isModificationPointDraggable: true,
      allowNoDoorsSelected: true,

      displayPerimeter: true,
      displayMagnification: true,
      displayModificationPoints: true,

      perimeterColor: HexColor.fromColor("#b2cfd1"),
      perimeterWidth: 0,
      perimeterSelectedColor: HexColor.fromColor("#004D51"),
      perimeterSelectedWidth: 2,

      modificationPointFillColor: new HexColor("#B7D2D5", 0.5),
      modificationPointRadius: 0,
      modificationPointBorderColor: new HexColor("#004D51", 0.7),
      modificationPointBorderWidth: 0,

      modificationPointArchedFillColor: new HexColor("#B7D2D5", 0.5),
      modificationPointArchedRadius: 0,
      modificationPointArchedBorderColor: new HexColor("#004D51", 0.7),
      modificationPointArchedBorderWidth: 0,

      modificationPointSelectedFillColor: new HexColor("#B7D2D5", 0.5),
      modificationPointSelectedRadius: 10,
      modificationPointSelectedBorderColor: new HexColor("#004D51", 0.7),
      modificationPointSelectedBorderWidth: 2,

      modificationPointSelectedArchedFillColor: new HexColor("#EB694F", 0.5),
      modificationPointSelectedArchedRadius: 10,
      modificationPointSelectedArchedBorderColor: new HexColor("#880808", 0.7),
      modificationPointSelectedArchedBorderWidth: 2,

      modificationMagnificationFactor: 3,
      modificationMagnificationFillColor: new HexColor("#B7D2D5", 0.5),
      modificationMagnificationRadius: 60,
      modificationMagnificationBorderColor: new HexColor("#004D51", 0.7),
      modificationMagnificationBorderWidth: 4,
      modificationMagnificationOffset: 20,
    };

    return loadDoorOptions;
  }
}
